import { deburr, get, isEmpty } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SidePanel from 'src/components/design-system/SidePanel';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import { useProductInformationPanel } from 'src/hooks/useProductInformationPanel';
import useWindowSize from 'src/hooks/useWindowSize';
import EditItemInformationDialog from 'src/sections/catalog_products/EditItemInformationDialog';
import EnlargedImageView from 'src/sections/catalog_products/EnlargedImageView';
import ExpandedDescriptionView from 'src/sections/catalog_products/ExpandedDescriptionView';
import { ProductData } from 'src/sections/catalog_products/useEditItemInformationForm';
import { useContextSelector } from 'use-context-selector';
import SearchProductInformationView from './SearchProductInformationView';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

interface SearchProductInformationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchProductInformationPanel({
  isOpen,
  onClose,
}: SearchProductInformationPanelProps) {
  const { t } = useTranslation();
  const { height, width } = useWindowSize();
  const {
    isImageEnlarged,
    isDescriptionExpanded,
    handleImageClick,
    handleCloseEnlargedImage,
    handleReadMoreClick,
    handleCloseDescription,
  } = useProductInformationPanel();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const editDialogCancelRef = useRef<(() => void) | null>(null);

  const updateProductSelection = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.updateProductSelection
  );
  const productDetails = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.productDetails
  );
  const selectedProduct = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.selectedProduct
  );
  const fetchProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.fetchProductDetails
  );
  const fetchProducts = useContextSelector(
    SearchProductQueryContext,
    (context) => context.fetchProducts
  );
  const productSelections = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.productSelections
  );
  const loadingProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.loadingProductDetails
  );
  const isAddingToFavorites = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.isAddingToFavorites
  );
  const handleSingleItemAddToCart = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleSingleItemAddToCart
  );
  const handleAddToFavorites = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleAddToFavorites
  );
  const folderOptions = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.folderOptions || []
  );
  const setSelectedProduct = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.setSelectedProduct
  );
  const getSelectedProductCatalogType = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.getSelectedProductCatalogType
  );
  const setIsSwapItemModalOpen = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.setIsSwapItemModalOpen
  );
  const setSwapItemData = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.setSwapItemData
  );

  const setQuantity = useCallback(
    (newQty: number) => {
      if (productDetails?.productKey) {
        updateProductSelection(productDetails.productKey, {
          qty: newQty,
          selected: newQty !== 0,
        });
      }
    },
    [updateProductSelection, productDetails?.productKey]
  );

  const { qty: quantity } = useMemo(
    () =>
      (productDetails?.productKey ? productSelections[productDetails.productKey] : null) || {
        selected: false,
        qty: 0,
      },
    [productSelections, productDetails?.productKey]
  );

  const getPanelTitle = useMemo(() => {
    if (isEditDialogOpen) {
      return t('Edit Item Information');
    }
    if (isDescriptionExpanded) {
      return t('Detailed Description');
    }
    if (isImageEnlarged) {
      return t('Item Image');
    }
    return t('Item Information');
  }, [isEditDialogOpen, isDescriptionExpanded, isImageEnlarged, t]);

  const handleEdit = useCallback(() => {
    setIsEditDialogOpen(true);
  }, []);

  const handleEditDialogClose = useCallback(() => {
    setIsEditDialogOpen(false);
  }, []);

  const handlePanelClose = useCallback(() => {
    if (isEditDialogOpen) {
      if (editDialogCancelRef.current) {
        editDialogCancelRef.current();
      } else {
        handleEditDialogClose();
      }
      return;
    }
    if (isDescriptionExpanded) {
      handleCloseDescription();
    } else if (isImageEnlarged) {
      handleCloseEnlargedImage();
    } else if (!loadingProductDetails) {
      setSelectedProduct(null);
      onClose();
    }
  }, [
    isEditDialogOpen,
    isDescriptionExpanded,
    isImageEnlarged,
    handleEditDialogClose,
    handleCloseDescription,
    handleCloseEnlargedImage,
    loadingProductDetails,
    setSelectedProduct,
    onClose,
  ]);

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(async () => {
    if (!productDetails) return;
    await handleSingleItemAddToCart({
      productId: productDetails.productId,
      catalogId: productDetails.catalogId,
      categoryId: productDetails.categoryId,
      quantity,
      isCompanySpecified: productDetails.isFormulary,
    });
    handlePanelClose();
  });

  const productDataForEdit: ProductData | null = useMemo(() => {
    if (!productDetails) return null;
    return {
      title: deburr(get(productDetails, 'description', '')).trim(),
      price: productDetails.price?.toString() || '',
      description: deburr(get(productDetails, 'info', '')).trim(),
      imageUrl: productDetails.url || '',
      productDetails: {
        'Item Name': deburr(get(productDetails, 'description', '')).trim(),
        Price: productDetails.price?.toString() || '',
        UPC: productDetails.upc || '',
        UNSPSC: productDetails.unspc || '',
      },
      manufacturingAndPackaging: {
        'Mfg Name': deburr(get(productDetails, 'manufacturerName', '')).trim(),
        'Mfg #': productDetails.manufacturerNumber || '',
        'Brand Name': deburr(get(productDetails, 'brandName', '')).trim(),
        UOM: productDetails.uom || '',
        'Case Pack Units': productDetails.casePack?.toString() || '',
        Weight: productDetails.weight || '',
      },
    };
  }, [productDetails]);

  const handleEditSaveSuccess = useCallback(async () => {
    await fetchProductDetails();
    await fetchProducts();
  }, [fetchProductDetails, fetchProducts]);

  const handleSwapItemClick = useCallback(() => {
    if (!productDetails) return;
    setIsSwapItemModalOpen(true);
    setSwapItemData({
      pageType: 3,
      supplierId: productDetails.supplierId,
      supplierCompanyId: productDetails.supplierCompanyId,
      itemNumber: productDetails.itemNum,
      productId: productDetails.productId,
      catalogId: productDetails.catalogId,
      categoryId: productDetails.categoryId,
      quantity,
      price: Number(productDetails.price),
      uom: productDetails.uom,
    });
    handlePanelClose();
  }, [
    productDetails,
    quantity,
    setIsSwapItemModalOpen,
    setSwapItemData,
    handlePanelClose,
  ]);

  return (
    <SidePanel
      title={getPanelTitle}
      isOpen={isOpen}
      onClose={handlePanelClose}
      width={width < 900 ? `${width}px` : '900px'}
      position="right"
      className={`z-[1000] bg-gray-100 ${
        width < 900 ? 'pb-[16px]' : 'pb-0'
      } absolute top-0 right-0 z-[1000]`}
      titleClassName="bg-[#0E80B4] px-[32px] -mx-[32px] text-white -mt-[24px] py-[8px] [&_button:focus-visible]:!outline [&_button:focus-visible]:!outline-2 [&_button:focus-visible]:!outline-offset-[-2px] [&_button:focus-visible]:!outline-white"
    >
      <div className="bg-gray-100" style={{ height: `${height - 100}px` }}>
        {isEditDialogOpen && productDataForEdit ? (
          <EditItemInformationDialog
            isOpen={isEditDialogOpen}
            onClose={handleEditDialogClose}
            productData={productDataForEdit}
            selectedProduct={selectedProduct}
            onSaveSuccess={handleEditSaveSuccess}
            onRequestClose={(cancelHandler) => {
              editDialogCancelRef.current = cancelHandler;
            }}
          />
        ) : loadingProductDetails ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">{t('Loading item details...')}</div>
          </div>
        ) : isEmpty(productDetails) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">{t('No item selected')}</div>
          </div>
        ) : isDescriptionExpanded ? (
          <ExpandedDescriptionView
            description={get(productDetails, 'info', '') as string}
            height={height}
            onClose={handleCloseDescription}
          />
        ) : isImageEnlarged ? (
          <EnlargedImageView
            imageUrl={get(productDetails, 'imageSrc', productDetails.url) as string}
            title={get(productDetails, 'imageDescription', productDetails.description) as string}
            height={height}
            onClose={handleCloseEnlargedImage}
          />
        ) : !isEmpty(productDetails) ? (
          <SearchProductInformationView
            productData={productDetails}
            catalogType={getSelectedProductCatalogType()}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onImageClick={handleImageClick}
            onReadMoreClick={handleReadMoreClick}
            onAddToCart={handleAddToCartClick}
            isAddToCartPending={isAddToCartPending}
            onAddToFavorites={handleAddToFavorites}
            onEdit={handleEdit}
            onSwapItemClick={handleSwapItemClick}
            isLoading={loadingProductDetails || isAddingToFavorites}
            folderOptions={folderOptions}
          />
        ) : null}
      </div>
    </SidePanel>
  );
}
